<template>
  <div class="upload-files-wrap">
    <div class="upload-files-header">
      <h3 class="upload-files-title">{{ $t('FILES.TITLE') }}</h3>
      <button-v2
        variant="filled"
        icon-left="upload"
        @click="showUploadModal = true"
      >
        {{ $t('FILES.UPLOAD_BUTTON') }}
      </button-v2>
    </div>

    <table-v2 v-if="files.length" :hover-rows="true">
      <template #header>
        <table-header-cell-v2
          :title="$t('FILES.TABLE.FILE_NAME')"
          width="40%"
          :is-dropdown-open="activeDropdownColumn === 'file_name'"
          @toggle-dropdown="toggleDropdown('file_name')"
          @close-dropdown="closeDropdown"
        >
          <template #dropdown>
            <table-dropdown-v2
              :show-filter="true"
              :filter-label="$t('FILES.DROPDOWN.FILTER_BY')"
              :sort-label="$t('FILES.DROPDOWN.SORT_BY')"
              :search-placeholder="$t('FILES.DROPDOWN.SEARCH_BY_FILE_NAME')"
              :filter-query="columnFilters.file_name"
              :sort-options="getSortOptions()"
              :current-sort="getCurrentSort('file_name')"
              @update:filterQuery="val => setFilter('file_name', val)"
              @sort="order => applySortFromDropdown('file_name', order)"
            />
          </template>
        </table-header-cell-v2>
        <table-header-cell-v2
          :title="$t('FILES.TABLE.COMMENTS')"
          :is-dropdown-open="activeDropdownColumn === 'comment'"
          @toggle-dropdown="toggleDropdown('comment')"
          @close-dropdown="closeDropdown"
        >
          <template #dropdown>
            <table-dropdown-v2
              :show-filter="true"
              :filter-label="$t('FILES.DROPDOWN.FILTER_BY')"
              :sort-label="$t('FILES.DROPDOWN.SORT_BY')"
              :search-placeholder="$t('FILES.DROPDOWN.SEARCH_BY_COMMENT')"
              :filter-query="columnFilters.comment"
              :sort-options="getSortOptions()"
              :current-sort="getCurrentSort('comment')"
              @update:filterQuery="val => setFilter('comment', val)"
              @sort="order => applySortFromDropdown('comment', order)"
            />
          </template>
        </table-header-cell-v2>
        <table-header-cell-v2
          :show-menu="false"
          :title="$t('FILES.TABLE.ACTION')"
          :width="120"
        />
      </template>
      <template #body>
        <table-row-v2 v-for="file in displayedFiles" :key="file.id">
          <table-cell-v2>
            <div class="uf-name-cell">
              <file-type-icon :file-name="getFileName(file)" :size="32" />
              <a
                v-if="getFileUrl(file)"
                :href="getFileUrl(file)"
                target="_blank"
                rel="noopener noreferrer"
                class="uf-filename-link"
              >
                {{ getFileName(file) }}
              </a>
              <span v-else class="uf-filename">{{ getFileName(file) }}</span>
            </div>
          </table-cell-v2>
          <table-cell-v2 class="uf-comment-td">
            <span v-if="editingFileId !== file.id">
              {{ file.comment || '' }}
            </span>
            <input
              v-else
              v-model="editComment"
              type="text"
              class="uf-comment-input"
              @keyup.enter="saveComment(file)"
              @blur="cancelEdit"
            />
          </table-cell-v2>
          <table-cell-v2>
            <div class="uf-actions">
              <button-v2
                variant="ghost"
                size="md"
                icon="edit"
                :title="$t('FILES.ACTIONS.EDIT_COMMENT')"
                @click="startEdit(file)"
              />
              <button-v2
                variant="ghost"
                size="md"
                icon="delete"
                color-scheme="danger"
                :title="$t('FILES.ACTIONS.DELETE_FILE')"
                @click="handleRemove(file)"
              />
            </div>
          </table-cell-v2>
        </table-row-v2>
        <table-row-v2 v-if="!displayedFiles.length">
          <table-cell-v2 colspan="3" align="center" class="uf-no-match">
            {{ $t('FILES.NO_MATCH') }}
          </table-cell-v2>
        </table-row-v2>
      </template>
    </table-v2>

    <div v-else class="uf-empty">
      <v2-icon icon="attach_file" :size="40" />
      <p>{{ $t('FILES.EMPTY_STATE') }}</p>
    </div>

    <transition name="modal-fade">
      <div
        v-if="showUploadModal"
        class="uf-modal-overlay"
        @click.self="closeModal"
      >
        <div class="uf-modal">
          <div class="uf-modal-header">
            <div class="uf-modal-header-top">
              <div class="uf-modal-title-row">
                <v2-icon icon="upload" class="text-gray-500" />
                <h3 class="uf-modal-title">{{ $t('FILES.MODAL.TITLE') }}</h3>
              </div>
              <button class="uf-modal-close" @click="closeModal">
                &times;
              </button>
            </div>
            <p class="uf-modal-subtitle !mt-0">
              {{ $t('FILES.MODAL.SUBTITLE') }}
            </p>
          </div>

          <div class="uf-modal-body">
            <div
              v-if="!attachedFile"
              class="mb-4 flex cursor-pointer flex-col items-center justify-center gap-3 rounded-[26px] border-2 border-dashed bg-white py-11 transition-colors"
              :class="
                isModalDragOver
                  ? 'border-brand/40'
                  : 'border-[#dfdcdc] hover:border-brand/40'
              "
              @click="triggerFileInput"
              @dragover.prevent="isModalDragOver = true"
              @dragleave="isModalDragOver = false"
              @drop.prevent="onModalDrop"
            >
              <div class="flex flex-col items-center text-center">
                <p class="text-[16px] leading-[1.2] text-[#323431]">
                  {{ $t('FILES.MODAL.DROPZONE_TITLE') }}
                </p>
                <p class="text-[14px] leading-[1.5] text-[#717171] mb-0">
                  {{ $t('FILES.MODAL.MAX_SIZE') }}
                </p>
              </div>
            </div>

            <div
              v-else
              class="relative mb-4 flex w-full items-center gap-3 rounded-[12px] border border-[#e5e5e5] bg-white p-3"
            >
              <file-type-icon :file-name="attachedFile.name" :size="40" />
              <div class="flex min-w-0 flex-1 flex-col gap-1">
                <p class="truncate text-[14px] leading-5 text-[#323431] !my-0">
                  {{ attachedFile.name }}
                </p>
                <p class="text-[12px] leading-[18px] text-[#5c5c5c] !my-0">
                  {{ formattedFileSize }}
                </p>
              </div>
              <button
                type="button"
                class="shrink-0 cursor-pointer self-start text-[#6b7280] transition-colors hover:text-[#323431]"
                :title="$t('FILES.MODAL.REMOVE_FILE')"
                @click.stop="clearAttached"
              >
                <v2-icon icon="close" :size="16" />
              </button>
            </div>
            <input
              ref="fileInput"
              type="file"
              :accept="allowedFileTypes"
              class="uf-hidden-input"
              @change="onFileSelected"
            />

            <div class="uf-modal-field">
              <input-v2
                v-model.trim="comment"
                :label="$t('FILES.MODAL.COMMENT_LABEL')"
                :placeholder="$t('FILES.MODAL.COMMENT_PLACEHOLDER')"
              />
            </div>
          </div>

          <div class="uf-modal-footer">
            <button-v2 variant="naked" size="lg" @click="closeModal">
              {{ $t('FILES.MODAL.CANCEL') }}
            </button-v2>
            <button-v2
              variant="filled"
              size="lg"
              :disabled="!attachedFile"
              @click="handleSubmit"
            >
              {{ $t('FILES.MODAL.UPLOAD') }}
            </button-v2>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  ALLOWED_FILE_TYPES,
  MAXIMUM_FILE_UPLOAD_SIZE,
} from 'shared/constants/contact';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import alertMixin from 'shared/mixins/alertMixin';
import V2Icon from 'v2/components/Icon.vue';
import FileTypeIcon from 'v2/components/FileTypeIcon.vue';
import ButtonV2 from 'v2/components/Button/ButtonV2.vue';
import InputV2 from 'v2/components/Input.vue';
import TableV2 from 'v2/components/Table/TableV2.vue';
import TableHeaderCellV2 from 'v2/components/Table/TableHeaderCellV2.vue';
import TableRowV2 from 'v2/components/Table/TableRowV2.vue';
import TableCellV2 from 'v2/components/Table/TableCellV2.vue';
import TableDropdownV2 from 'v2/components/Table/TableDropdownV2.vue';

export default {
  name: 'PrivateFilesOnContactPage',
  components: {
    V2Icon,
    FileTypeIcon,
    ButtonV2,
    InputV2,
    TableV2,
    TableHeaderCellV2,
    TableRowV2,
    TableCellV2,
    TableDropdownV2,
  },
  mixins: [alertMixin],
  props: {
    contactId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      comment: '',
      attachedFile: null,
      files: [],
      showUploadModal: false,
      isModalDragOver: false,
      editingFileId: null,
      editComment: '',
      activeDropdownColumn: null,
      columnFilters: { file_name: '', comment: '' },
      sortConfig: {},
    };
  },
  computed: {
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    formattedFileSize() {
      if (!this.attachedFile) return '';
      const bytes = this.attachedFile.size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    },
    displayedFiles() {
      let result = this.files.slice();
      const nameQuery = (this.columnFilters.file_name || '')
        .trim()
        .toLowerCase();
      const commentQuery = (this.columnFilters.comment || '')
        .trim()
        .toLowerCase();
      if (nameQuery) {
        result = result.filter(file =>
          this.getFileName(file).toLowerCase().includes(nameQuery)
        );
      }
      if (commentQuery) {
        result = result.filter(file =>
          (file.comment || '').toLowerCase().includes(commentQuery)
        );
      }
      const sortEntry = Object.entries(this.sortConfig)[0];
      if (sortEntry) {
        const [field, order] = sortEntry;
        result.sort((a, b) => {
          const valA =
            field === 'file_name'
              ? this.getFileName(a).toLowerCase()
              : (a.comment || '').toLowerCase();
          const valB =
            field === 'file_name'
              ? this.getFileName(b).toLowerCase()
              : (b.comment || '').toLowerCase();
          if (valA < valB) return order === 'asc' ? -1 : 1;
          if (valA > valB) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }
      return result;
    },
  },
  mounted() {
    this.fetchFiles();
  },
  methods: {
    async fetchFiles() {
      const { contactId: id } = this;
      this.files = await this.$store.dispatch('contacts/fetchFiles', { id });
    },
    triggerFileInput() {
      if (!this.attachedFile) {
        this.$refs.fileInput.click();
      }
    },
    onModalDrop(e) {
      this.isModalDragOver = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.processFile(file);
      }
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (file) {
        this.processFile(file);
      }
      this.$refs.fileInput.value = '';
    },
    processFile(file) {
      if (checkFileSizeLimit({ file }, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.attachedFile = file;
      } else {
        this.showAlert(
          this.$t('FILES.ALERT.ERROR.FILE_SIZE', {
            size: MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    clearAttached() {
      this.attachedFile = null;
    },
    closeModal() {
      this.showUploadModal = false;
      this.attachedFile = null;
      this.comment = '';
      this.isModalDragOver = false;
    },
    handleSubmit() {
      if (!this.attachedFile) return;
      this.onSubmit();
    },
    async handleRemove(file) {
      try {
        this.files = await this.$store.dispatch('contacts/removeFile', {
          id: this.contactId,
          fileId: file.id,
        });
        this.showAlert(this.$t('FILES.ALERT.SUCCESS.DELETE'));
      } catch (error) {
        this.showAlert(error.message);
      }
    },
    async onSubmit() {
      try {
        this.files = await this.$store.dispatch('contacts/addFile', {
          id: this.contactId,
          isFormData: true,
          file: this.attachedFile,
          comment: this.comment,
          additional_attributes: { social_profiles: {} },
        });
        this.showAlert(this.$t('FILES.ALERT.SUCCESS.ADD'));
        this.closeModal();
      } catch (error) {
        this.showAlert(error.message);
      }
    },
    startEdit(file) {
      this.editingFileId = file.id;
      this.editComment = file.comment || '';
    },
    cancelEdit() {
      this.editingFileId = null;
      this.editComment = '';
    },
    saveComment(file) {
      this.cancelEdit();
    },
    toggleDropdown(key) {
      this.activeDropdownColumn =
        this.activeDropdownColumn === key ? null : key;
    },
    closeDropdown() {
      this.activeDropdownColumn = null;
    },
    setFilter(key, val) {
      this.columnFilters = { ...this.columnFilters, [key]: val };
    },
    getSortOptions() {
      return [
        { value: 'asc', label: 'A→Z' },
        { value: 'desc', label: 'Z→A' },
      ];
    },
    getCurrentSort(key) {
      return this.sortConfig[key] || '';
    },
    applySortFromDropdown(key, order) {
      this.sortConfig = { [key]: order };
      this.closeDropdown();
    },
    getFileName(file) {
      if (file.data_url) {
        const parts = file.data_url.split('/');
        return decodeURIComponent(parts[parts.length - 1]) || 'file';
      }
      if (file.file_name) return file.file_name;
      if (file.name) return file.name;
      return 'file';
    },
    getFileUrl(file) {
      return file.data_url || file.url || '';
    },
  },
};
</script>

<style lang="scss" scoped>
.upload-files-wrap {
  position: relative;
  min-height: 200px;
}

.upload-files-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.upload-files-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.uf-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uf-filename-link {
  min-width: 0;
  color: #2563eb;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.uf-filename {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uf-comment-td {
  color: #6b7280;
}

.uf-comment-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2abb7f;
  }
}

.uf-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.uf-no-match {
  color: #9ca3af;
}

.uf-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;

  p {
    margin: 12px 0 0;
    font-size: 14px;
  }
}

.uf-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.uf-modal {
  background: #fff;
  border-radius: 12px;
  width: 600px;
  max-width: 90vw;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.uf-modal-header {
  display: flex;
  flex-direction: column;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f3f4f6;
  box-shadow: 0 1px 2px rgba(202, 202, 202, 0.16);
}

.uf-modal-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.uf-modal-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uf-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.uf-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #6b7280;
  line-height: 1;

  &:hover {
    background: #f3f4f6;
  }
}

.uf-modal-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}

.uf-modal-body {
  padding: 16px 24px;
}

.uf-hidden-input {
  display: none;
}

.uf-modal-field {
  margin-bottom: 0;
}

.uf-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #f3f4f6;
  box-shadow: 0 -1px 2px rgba(202, 202, 202, 0.16);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
